import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleCSE } from '../hooks/useGoogleCSE';
import { searchService } from '../services/searchService';
import './SearchDrawer.css';

export default function SearchDrawer({ open, query, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [lastQuery, setLastQuery] = useState('');
  const [searchState, setSearchState] = useState({
    aiAnswer: '',
    loading: false,
    error: '',
    results: [],
    intent: '',
    groundingMetadata: null,
    aiLoading: false
  });

  const navigate = useNavigate();
  const { containerRef, createSearchContainer, extractGoogleResults, triggerSearch } = useGoogleCSE();
  const DRAWER_CONTAINER_ID = 'drawer-cse-container';

  // Initialize search query when drawer opens or query changes
  useEffect(() => {
    if (query) {
      setSearchQuery(query);
      setLastQuery(query);
    }
  }, [query]);

  // Create search container when drawer opens
  useEffect(() => {
    if (open) {
      createSearchContainer(DRAWER_CONTAINER_ID);
    }
  }, [open, createSearchContainer]);

  // Perform search when query changes and drawer is open
  // FIXED: Removed auto-search on typing to prevent API flood and queue backlog.
  // Search now triggers only on Enter or Button click.
  /*
  useEffect(() => {
    if (open && searchQuery.trim()) {
      handleSearch(searchQuery);
    }
  }, [open, searchQuery]);
  */

  // ⭐ REMOVED: Body scroll prevention - now you can scroll while drawer is open
  // useEffect(() => {
  //   document.body.style.overflow = open ? 'hidden' : '';
  //   return () => { document.body.style.overflow = ''; };
  // }, [open]);

  // Handle click outside to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [open, onClose]);

  const handleSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return;

    setSearchState(prev => ({
      ...prev,
      loading: true,
      error: '',
      aiAnswer: '',
      results: [],
      intent: '',
      groundingMetadata: null
    }));

    try {
      triggerSearch(searchTerm, DRAWER_CONTAINER_ID);
      const data = await searchService.performUnifiedSearch(searchTerm);

      if (data.error) {
        setSearchState(prev => ({ ...prev, error: data.error, loading: false }));
        return;
      }

      setSearchState(prev => ({
        ...prev,
        intent: data.intent,
        groundingMetadata: data.groundingMetadata,
        loading: false
      }));

      setTimeout(() => {
        const googleResults = extractGoogleResults(DRAWER_CONTAINER_ID);
        const combinedResults = googleResults.length > 0 ? googleResults : data.searchResults || [];
        setSearchState(prev => ({ ...prev, results: combinedResults }));
      }, 1500);

      if (data.intent === 'AI_RESPONSE' && data.answer) {
        setSearchState(prev => ({
          ...prev,
          aiAnswer: searchService.cleanAIResponse(data.answer)
        }));
      }

    } catch (err) {
      console.error('Search error:', err);
      setSearchState(prev => ({
        ...prev,
        error: 'Search failed. Please try again.',
        loading: false
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setLastQuery(searchQuery);
      handleSearch(searchQuery);
    }
  };

  const { aiAnswer, loading, error, results, intent, groundingMetadata } = searchState;

  return (
    <>
      {open && (
        <div
          className="drawer-backdrop"
          onClick={handleBackdropClick}
        />
      )}

      {!open && lastQuery && (
        <button
          className="drawer-reopen-btn"
          onClick={() => onClose(false)}
          title={`Reopen search: ${lastQuery}`}
        >
          <span className="reopen-icon">🔍</span>
          <span className="reopen-text">Search</span>
        </button>
      )}

      <div className={`search-drawer ${open ? 'open' : ''}`}>
        <button className="drawer-close" onClick={onClose}>✕</button>

        <form onSubmit={handleSubmit} className="drawer-search-form">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search NBA Finals articles or ask a question..."
            className="search-input"
          />
          <button type="submit" className="search-button" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button
            type="button"
            className="drawer-expand"
            onClick={() => navigate(`/search?q=${encodeURIComponent(searchQuery)}`)}
          >
            Open full page →
          </button>
        </form>

        <div className="drawer-body">
          {searchState.aiLoading && (
            <div className="google-lima-container">
              <div className="lima-header">
                <div className="lima-icon">✨</div>
                <h3 className="lima-title">Thinking...</h3>
              </div>
              <div className="lima-content skeleton-pulse" style={{ height: '60px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}></div>
            </div>
          )}

          {intent === 'AI_RESPONSE' && aiAnswer && (
            <div className="google-lima-container">
              <div className="lima-header">
                <div className="lima-icon">🤖</div>
                <h3 className="lima-title">Google Lima</h3>
              </div>
              <div className="lima-content">{aiAnswer}</div>
              {groundingMetadata?.webSearchQueries && (
                <div className="lima-sources">
                  <small>Sources: Web search and NBA Finals articles</small>
                </div>
              )}
            </div>
          )}

          {results.length > 0 && (
            <div className="search-results-container">
              <h3 className="results-title">
                {intent === 'AI_RESPONSE' ? 'Related Articles' : 'Search Results'}
              </h3>
              <div className="search-results-grid">
                {results.slice(0, 6).map((result, idx) => (
                  <div
                    key={idx}
                    className="search-result-card"
                    onClick={() => window.open(result.link, '_blank')}
                  >
                    <h4 className="result-title">{result.title}</h4>
                    <p className="result-snippet">{result.snippet}</p>
                    <div className="result-url">{result.link}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <span>Analyzing your search and finding the best results...</span>
            </div>
          )}
        </div>

        <div ref={containerRef} className="google-cse-hidden"></div>
      </div>
    </>
  );
}
